import { TCurrentUser } from 'types/group';
import { Provider } from 'types/provider';

/**
 * True when an OPS user is previewing a provider's portal. In this mode the
 * sa-token ACTOR is the OPS user, so `currentUser.user` is the OPS account and
 * `currentUser.actingUser` is the provider being previewed.
 *
 * Note this is deliberately narrower than `currentUser.impersonated`, which is
 * also true for DELEGATED (a group-practice staff member acting for a
 * provider). Under DELEGATED the actor is a real portal user whose own account
 * details are the correct thing to show and edit.
 */
export const isOpsImpersonate = (currentUser: TCurrentUser | undefined): boolean =>
    currentUser?.actingMode === 'OPS_IMPERSONATE';

export const getDisplayInfo = (currentUser: TCurrentUser | undefined): Provider | undefined => {
    if (!currentUser) return undefined;
    const isImpersonate = isOpsImpersonate(currentUser);
    if (isImpersonate) {
        const { actingUser, provider } = currentUser;
        return {
            id: actingUser.id,
            firstName: actingUser.firstName,
            lastName: actingUser.lastName,
            email: actingUser.email,
            tel: '',
            address: '',
            specialityInHealth: provider.specialties || '',
            specialityInFamily: '',
            state: '',
            yearExp: 0,
            zip: '',
            photo: '',
            credential: provider.credential || '',
        };
    }
    const source = currentUser.actingUser.id !== currentUser.user.id
        ? currentUser.user
        : currentUser.actingUser;
    return {
        id: source.id,
        firstName: source.firstName,
        lastName: source.lastName,
        email: source.email,
        tel: (source as TCurrentUser['user']).tel || '',
        address: '',
        specialityInHealth: '',
        specialityInFamily: '',
        state: '',
        yearExp: 0,
        zip: '',
        photo: '',
        credential: '',
    };
};
